import React, { useState, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";

const tourSteps = [
  {
    targetId: "app-header",
    title: "Welcome to Leitner Prep!",
    content:
      "This quick tour will guide you through the main features of the app. Let's get started!",
    mode: "creator",
    position: "bottom",
  },
  {
    targetId: "nav-creator-btn",
    title: "1. Create Questions",
    content:
      "You start here, in the Creator View. This is where you can create your own study questions.",
    mode: "creator",
    position: "bottom",
  },
  {
    targetId: "creator-form",
    title: "Question Form",
    content:
      "Use this form to add new questions. You can create simple multiple-choice questions or more complex 'stem' questions.",
    mode: "creator",
    position: "right",
  },
  {
    targetId: "question-bank",
    title: "Question Bank",
    content:
      "All questions you create will appear here. You can edit or delete them at any time.",
    mode: "creator",
    position: "left",
  },
  {
    targetId: "nav-learner-btn",
    title: "2. Leitner Study",
    content:
      "Now the core of the app: the Leitner System for spaced repetition.",
    mode: "learner",
    position: "bottom",
  },
  {
    targetId: "leitner-boxes",
    title: "The Leitner Boxes",
    content:
      "Questions start in Box A. Answer correctly to move them up a box.",
    mode: "learner",
    position: "bottom",
  },
  {
    targetId: "session-setup",
    title: "Study Sessions",
    content:
      "The system schedules which boxes to study each day to maximize retention.",
    mode: "learner",
    position: "right",
  },
  {
    targetId: "nav-test-btn",
    title: "3. Test Center",
    content:
      "Build and take practice tests to track your knowledge and confidence.",
    mode: "test",
    position: "bottom",
  },
  {
    targetId: "create-test-btn",
    title: "Create & Take Tests",
    content:
      "Create custom tests from your question bank with time limits and scoring.",
    mode: "test",
    position: "bottom",
  },
  {
    targetId: "performance-summary",
    title: "Track Your Performance",
    content:
      "After taking tests, track your progress over time using visual summaries.",
    mode: "test",
    position: "top",
  },
  {
    targetId: "app-header",
    title: "You're all set!",
    content:
      "That's it! Start creating or studying anytime. Good luck on your learning journey!",
    mode: "learner",
    position: "bottom",
  },
];

const OnboardingTour = ({ isOpen, onClose, setAppMode }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = tourSteps[currentStep];

  /** Switch mode when needed **/
  useEffect(() => {
    if (isOpen && step?.mode) {
      setAppMode(step.mode);
    }
  }, [step?.mode, isOpen, setAppMode]);

  /** Find target element and compute highlight box **/
  useEffect(() => {
    if (!isOpen || !step) return;

    let scrollTimeout;

    const onScroll = () => {
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        const el = document.getElementById(step.targetId);
        if (!el) return;

        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height,
        });
      }, 120); // Fires AFTER scrolling stops
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener("scroll", onScroll);
    };
  }, [isOpen, currentStep, step]);


  /** Reposition popup on scroll **/
  useEffect(() => {
    if (!isOpen || !targetRect) return;

    const handleScroll = () => setTargetRect((prev) => ({ ...prev }));
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isOpen, targetRect]);

  /** Compute popup position **/
  const getPopupPosition = () => {
    if (!targetRect) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        position: "fixed",
      };
    }

    const offset = 16;

    const rawTop = targetRect.top;
    const rawLeft = targetRect.left;

    const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

    const safeTop = clamp(
      rawTop,
      window.scrollY + 40,
      window.scrollY + window.innerHeight - 200
    );

    const safeLeft = clamp(
      rawLeft,
      window.scrollX + 40,
      window.scrollX + window.innerWidth - 200
    );

    const { width, height } = targetRect;

    const positions = {
      top: {
        top: safeTop - offset,
        left: safeLeft + width / 2,
        transform: "translate(-50%, -100%)",
      },
      bottom: {
        top: safeTop + height + offset,
        left: safeLeft + width / 2,
        transform: "translate(-50%, 0)",
      },
      left: {
        top: safeTop + height / 2,
        left: safeLeft - offset,
        transform: "translate(-100%, -50%)",
      },
      right: {
        top: safeTop + height / 2,
        left: safeLeft + width + offset,
        transform: "translate(0, -50%)",
      },
    };

    return {
      ...positions[step.position || "bottom"],
      position: "absolute",
      zIndex: 50,
    };
  };

  const handleNext = () =>
    currentStep < tourSteps.length - 1
      ? setCurrentStep((n) => n + 1)
      : onClose();

  const handlePrev = () =>
    currentStep > 0 && setCurrentStep((n) => n - 1);

  if (!isOpen) return null;

  return (
    typeof window !== "undefined"
      ? ReactDOM.createPortal(
          <div className="fixed inset-0 z-[99999] pointer-events-none">

            {/* BACKDROP */}
            <div className="fixed inset-0 bg-black/60 z-[99990]"></div>

            {/* HIGHLIGHT */}
            {targetRect && (
              <div
                className="absolute z-[99995] rounded-lg border-2 border-indigo-400 transition-all duration-300"
                style={{
                  top: targetRect.top - 8,
                  left: targetRect.left - 8,
                  width: targetRect.width + 16,
                  height: targetRect.height + 16,
                }}
              />
            )}

            {/* POPUP */}
            <div
                className="absolute bg-slate-800 border border-slate-600 rounded-lg p-5 shadow-2xl w-80 pointer-events-auto z-[100000]"
                style={getPopupPosition()}
              >

              <h3 className="text-lg font-bold text-indigo-400 mb-2">
                {step.title}
              </h3>

              <p className="text-slate-300 text-sm mb-4">{step.content}</p>

              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">
                  {currentStep + 1} / {tourSteps.length}
                </span>

                <div className="space-x-2">
                  {currentStep > 0 && (
                    <button
                      onClick={handlePrev}
                      className="text-sm text-slate-300 hover:text-white px-3 py-1 bg-slate-700/40 rounded-md"
                    >
                      Prev
                    </button>
                  )}

                  <button
                    onClick={handleNext}
                    className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-4 py-2 rounded-md"
                  >
                    {currentStep === tourSteps.length - 1
                      ? "Finish"
                      : "Next"}
                  </button>
                </div>
              </div>

              <button
                onClick={onClose}
                className="absolute top-2 right-2 text-slate-500 hover:text-slate-300 text-xl"
              >
                ×
              </button>
            </div>
          </div>,
          document.body
        )
      : null
  );

};

export default OnboardingTour;
